import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { API } from "@/lib/api";
import { CalendarDays, MapPin, Trophy, Target, Zap, Square, Download, Share2 } from "lucide-react";
import { toast } from "sonner";

function formatDate(iso) {
  return new Date(iso).toLocaleDateString("fr-FR", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

export default function Officials() {
  const [matches, setMatches] = useState([]);
  const [details, setDetails] = useState({}); // {match_id: fullMatch}
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("finished"); // finished | upcoming

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/matches?match_type=official");
        setMatches(data);
        // load details for finished ones (players + stats)
        const finished = data.filter((m) => m.status === "finished");
        const results = await Promise.all(
          finished.map((m) => api.get(`/matches/${m.id}`).then((r) => [m.id, r.data]).catch(() => null)),
        );
        const map = {};
        results.filter(Boolean).forEach(([id, full]) => {
          map[id] = full;
        });
        setDetails(map);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const finished = matches.filter((m) => m.status === "finished");
  const upcoming = matches.filter((m) => m.status !== "finished");

  const wins = finished.filter((m) => (m.our_score ?? 0) > (m.opponent_score ?? 0)).length;
  const draws = finished.filter((m) => (m.our_score ?? 0) === (m.opponent_score ?? 0)).length;
  const losses = finished.filter((m) => (m.our_score ?? 0) < (m.opponent_score ?? 0)).length;
  const goalsFor = finished.reduce((sum, m) => sum + (m.our_score ?? 0), 0);
  const goalsAgainst = finished.reduce((sum, m) => sum + (m.opponent_score ?? 0), 0);

  const list = tab === "finished" ? finished : upcoming;

  return (
    <div className="space-y-8" data-testid="officials-page">
      <div>
        <div className="text-[11px] font-mono-stat uppercase tracking-[0.25em] text-[#E5B85C] mb-2">
          Compétition
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl uppercase tracking-tight">
          Matchs officiels
        </h1>
        <p className="text-sm text-slate-400 mt-2 max-w-2xl">
          Résultats du club contre les équipes extérieures — score, adversaire et présence de chaque joueur avec ses stats.
        </p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <RecordCard label="Victoires" value={wins} color="#22C55E" testid="off-wins" />
        <RecordCard label="Nuls" value={draws} color="#94A3B8" testid="off-draws" />
        <RecordCard label="Défaites" value={losses} color="#EF4444" testid="off-losses" />
        <RecordCard label="Buts pour" value={goalsFor} color="#E5B85C" testid="off-gf" />
        <RecordCard label="Buts contre" value={goalsAgainst} color="#F87171" testid="off-ga" />
      </div>

      <div className="flex flex-wrap gap-1 p-1 rounded-xl bg-slate-900 border border-white/10 w-fit">
        {[
          { key: "finished", label: `Terminés (${finished.length})` },
          { key: "upcoming", label: `À venir (${upcoming.length})` },
        ].map((t) => (
          <button
            key={t.key}
            data-testid={`off-tab-${t.key}`}
            onClick={() => setTab(t.key)}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide ${
              tab === t.key
                ? "bg-[#E5B85C] text-[#0B0F17]"
                : "text-slate-300 hover:bg-slate-800"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-slate-400 text-sm font-mono-stat uppercase">Chargement...</div>
      ) : list.length === 0 ? (
        <div className="p-8 rounded-2xl border border-dashed border-white/10 text-center text-slate-500">
          {tab === "finished"
            ? "Aucun match officiel terminé pour le moment"
            : "Aucun match officiel à venir"}
        </div>
      ) : (
        <div className="space-y-4">
          {list.map((m) => (
            <OfficialMatchCard key={m.id} match={m} detail={details[m.id]} tab={tab} />
          ))}
        </div>
      )}
    </div>
  );
}

function OfficialMatchCard({ match, detail, tab }) {
  const isFinished = match.status === "finished";
  const our = match.our_score ?? 0;
  const opp = match.opponent_score ?? 0;
  const outcome = isFinished
    ? our > opp
      ? { label: "V", color: "#22C55E" }
      : our < opp
      ? { label: "D", color: "#EF4444" }
      : { label: "N", color: "#94A3B8" }
    : null;

  const players = detail?.players || [];
  const stats = detail?.stats || [];
  const availabilities = detail?.availabilities || [];
  const motmPlayer = players.find((p) => p.id === match.man_of_the_match);

  // Players who played (played=true) or were listed in availability=available
  const rows = stats.length > 0
    ? stats.map((s) => {
        const p = players.find((pp) => pp.id === s.player_id);
        return { ...s, player: p };
      })
    : availabilities
        .filter((a) => a.status === "available")
        .map((a) => ({
          player_id: a.player_id,
          player: players.find((pp) => pp.id === a.player_id),
          goals: 0,
          assists: 0,
          yellow_cards: 0,
          red_cards: 0,
          played: true,
          observation: "",
        }));

  return (
    <div
      data-testid={`off-match-${match.id}`}
      className="rounded-2xl bg-slate-900 border border-white/10 overflow-hidden"
    >
      <div className="p-5 border-b border-white/10">
        <div className="flex items-start gap-4 flex-wrap justify-between">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded bg-[#E5B85C]/20 text-[#E5B85C] border border-[#E5B85C]/40">
                Officiel
              </span>
              {outcome && (
                <span
                  className="text-[9px] font-mono-stat uppercase tracking-widest px-1.5 py-0.5 rounded"
                  style={{
                    background: `${outcome.color}20`,
                    color: outcome.color,
                    border: `1px solid ${outcome.color}55`,
                  }}
                >
                  {outcome.label === "V"
                    ? "Victoire"
                    : outcome.label === "D"
                    ? "Défaite"
                    : "Nul"}
                </span>
              )}
            </div>
            <Link
              to={`/matches/${match.id}`}
              className="font-display font-black text-xl sm:text-2xl uppercase tracking-tight hover:text-[#E5B85C]"
            >
              {match.title ? `${match.title} · ` : ""}vs {match.opponent_name || "Adversaire"}
            </Link>
            <div className="flex flex-wrap gap-3 text-xs text-slate-400 font-mono-stat mt-2">
              <span className="flex items-center gap-1">
                <CalendarDays className="w-3 h-3" /> {formatDate(match.match_date)}
              </span>
              <span className="flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {match.location}
              </span>
            </div>
          </div>
          {isFinished && (
            <div className="font-mono-stat font-black text-2xl sm:text-3xl flex items-center gap-3 whitespace-nowrap">
              <span className="text-[#E5B85C]">Nous {our}</span>
              <span className="text-slate-600">—</span>
              <span className="text-slate-300">
                {opp} {match.opponent_name || "Adv."}
              </span>
            </div>
          )}
        </div>
        {motmPlayer && (
          <div className="mt-3 inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-[#E5B85C]/15 border border-[#E5B85C]/40">
            <Trophy className="w-3.5 h-3.5 text-[#E5B85C]" />
            <span className="text-[10px] font-mono-stat uppercase tracking-widest text-[#E5B85C]">
              Homme du match
            </span>
            <span className="font-bold text-xs text-white">{motmPlayer.name}</span>
          </div>
        )}
        {isFinished && (
          <div className="mt-3 flex flex-wrap gap-2">
            <ShareButtons match={match} outcome={outcome} />
          </div>
        )}
      </div>

      {isFinished && (
        <div>
          <div className="px-5 py-3 border-b border-white/5 flex items-center justify-between">
            <div className="font-display font-bold text-sm uppercase tracking-wide">
              Joueurs · {rows.length}
            </div>
            <div className="hidden sm:flex items-center gap-4 text-[10px] font-mono-stat uppercase tracking-widest text-slate-500">
              <span className="flex items-center gap-1">
                <Target className="w-3 h-3 text-[#E5B85C]" /> Buts
              </span>
              <span className="flex items-center gap-1">
                <Zap className="w-3 h-3 text-sky-400" /> Passes
              </span>
              <span className="flex items-center gap-1">
                <Square className="w-3 h-3 text-yellow-400" /> Jaunes
              </span>
              <span className="flex items-center gap-1">
                <Square className="w-3 h-3 text-red-500" /> Rouges
              </span>
            </div>
          </div>
          <div className="divide-y divide-white/5">
            {rows.length === 0 && (
              <div className="p-6 text-center text-sm text-slate-500">
                Feuille de match non renseignée
              </div>
            )}
            {rows.map((r) => (
              <div
                key={r.player_id}
                data-testid={`off-player-${match.id}-${r.player_id}`}
                className="p-3 flex items-center gap-3 flex-wrap"
              >
                <div className="w-9 h-9 rounded bg-slate-800 flex items-center justify-center font-mono-stat font-bold text-xs">
                  {r.player?.jersey_number || "-"}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm truncate">
                    {r.player?.name || "Joueur inconnu"}
                  </div>
                  <div className="text-xs text-slate-500 truncate">
                    {r.player?.position || "—"}
                    {r.observation ? ` · ${r.observation}` : ""}
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs font-mono-stat">
                  <Stat value={r.goals} color="#E5B85C" icon={Target} />
                  <Stat value={r.assists} color="#60A5FA" icon={Zap} />
                  <Stat value={r.yellow_cards} color="#EAB308" icon={Square} />
                  <Stat value={r.red_cards} color="#EF4444" icon={Square} />
                  {!r.played && (
                    <span className="text-[9px] uppercase tracking-widest text-slate-500 px-1.5 py-0.5 rounded border border-white/10">
                      Non joué
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isFinished && (
        <div className="p-5 text-sm text-slate-400">
          Match à venir · <Link className="text-[#E5B85C] hover:underline" to={`/matches/${match.id}`}>Gérer les présences</Link>
        </div>
      )}
    </div>
  );
}

function Stat({ value, color, icon: Icon }) {
  return (
    <span className="inline-flex items-center gap-1 font-bold" style={{ color }}>
      <Icon className="w-3 h-3" /> {value || 0}
    </span>
  );
}

function ShareButtons({ match, outcome }) {
  const [busy, setBusy] = useState(false);

  const fetchCard = async () => {
    const res = await fetch(`${API}/matches/${match.id}/share-card.png`, {
      credentials: "include",
    });
    if (!res.ok) throw new Error("card failed");
    return res.blob();
  };

  const shareLabel = outcome?.label === "V" ? "Victoire" : outcome?.label === "D" ? "Défaite" : "Match nul";
  const opp = match.opponent_name || "Adversaire";
  const scoreText = `Nous ${match.our_score ?? 0} - ${match.opponent_score ?? 0} ${opp}`;
  const text = `${shareLabel} ! ${match.title || "Match officiel"} · ${scoreText} — ASS les Régionnaires`;

  const download = async () => {
    setBusy(true);
    try {
      const blob = await fetchCard();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ass-regionnaires-${match.id}.png`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
      toast.success("Image téléchargée");
    } catch (e) {
      toast.error("Erreur de génération");
    } finally {
      setBusy(false);
    }
  };

  const share = async () => {
    setBusy(true);
    try {
      const blob = await fetchCard();
      const file = new File([blob], "match.png", { type: "image/png" });
      const data = { title: "ASS les Régionnaires", text, files: [file] };
      if (navigator.canShare && navigator.canShare(data)) {
        await navigator.share(data);
      } else if (navigator.share) {
        await navigator.share({ title: "ASS les Régionnaires", text });
      } else {
        await download();
      }
    } catch (e) {
      // user cancelled or unsupported
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`share-match-${match.id}`}
        onClick={share}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-[#E5B85C] hover:bg-[#D4A24E] text-[#0B0F17] text-xs font-bold uppercase tracking-wide disabled:opacity-60"
      >
        <Share2 className="w-3.5 h-3.5" /> Partager
      </button>
      <button
        type="button"
        data-testid={`download-match-${match.id}`}
        onClick={download}
        disabled={busy}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-700 text-slate-200 text-xs font-bold uppercase tracking-wide disabled:opacity-60"
      >
        <Download className="w-3.5 h-3.5" /> PNG
      </button>
    </>
  );
}

function RecordCard({ label, value, color, testid }) {
  return (
    <div
      data-testid={testid}
      className="p-4 rounded-xl bg-slate-900 border border-white/10 relative overflow-hidden"
    >
      <div className="absolute top-0 left-0 h-1 w-full" style={{ background: color }} />
      <div className="text-[10px] font-mono-stat uppercase tracking-widest text-slate-400">
        {label}
      </div>
      <div className="font-display font-black text-3xl mt-1" style={{ color }}>
        {value}
      </div>
    </div>
  );
}
